import {
  BeaconReadingsCellRenderer,
  IconValueCellRenderer,
  LinkCellRenderer,
  OnlineStatusCellRenderer,
} from '@components';
import { ABILITY_MAP } from '../../constants';
import { getFormattedDateTime } from '../../utils';

export const placeListColumnDefs = [
  {
    headerName: 'Place',
    field: 'name',
    colId: 'name',
    cellRenderer: LinkCellRenderer,
    cellRendererParams: ({ data }) => ({
      to: `/places/${data.id}`,
      text: data.name,
    }),
  },
  {
    headerName: 'Map',
    field: 'mapName',
  },
  {
    headerName: 'Assets',
    field: 'assets',
    cellRenderer: IconValueCellRenderer,
    cellRendererParams: { iconName: 'asset' },
  },
  {
    headerName: 'Stationary Assets',
    field: 'stationaryAssets',
    cellRenderer: IconValueCellRenderer,
    cellRendererParams: { iconName: 'stationary-beacon' },
  },
];

const getAbilityReading = (data, abilityName) => {
  return data.readings?.find((reading) => reading.ability === abilityName);
};

const getPressureReading = (data) => {
  const regex = /pressure/i;
  return data.readings?.find((reading) => regex.test(reading.ability));
};

const getSortableNumber = (reading) => {
  if (!reading) return null;

  // adjust this if your raw numeric field is named differently
  const rawValue = reading.value ?? reading.reading ?? reading.rawValue;

  const num = Number(rawValue);
  return Number.isFinite(num) ? num : null;
};

const compareNullableNumbers = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
};

export const stationaryAssetsColumnDefs = [
  {
    headerName: 'Stationary Assets',
    field: 'assetName',
    colId: 'assetName',
    sortable: true,
    sortingOrder: ['asc', 'desc', null],
  },
  {
    headerName: 'Battery',
    sortable: true,
    valueGetter(params) {
      const reading = getAbilityReading(params.data, 'Battery');
      return reading ? ABILITY_MAP[reading.ability].value(reading) : '--';
    },
    comparator: (_valueA, _valueB, nodeA, nodeB) => {
      const a = getSortableNumber(getAbilityReading(nodeA.data, 'Battery'));
      const b = getSortableNumber(getAbilityReading(nodeB.data, 'Battery'));
      return compareNullableNumbers(a, b);
    },
  },
  {
    headerName: 'Temperature',
    sortable: true,
    valueGetter(params) {
      const reading = getAbilityReading(params.data, 'Temperature');
      return reading ? ABILITY_MAP[reading.ability].value(reading) : '--';
    },
    comparator: (_valueA, _valueB, nodeA, nodeB) => {
      const a = getSortableNumber(getAbilityReading(nodeA.data, 'Temperature'));
      const b = getSortableNumber(getAbilityReading(nodeB.data, 'Temperature'));
      return compareNullableNumbers(a, b);
    },
  },
  {
    headerName: 'Humidity',
    sortable: true,
    valueGetter(params) {
      const reading = getAbilityReading(params.data, 'Humidity');
      return reading ? ABILITY_MAP[reading.ability].value(reading) : '--';
    },
    comparator: (_valueA, _valueB, nodeA, nodeB) => {
      const a = getSortableNumber(getAbilityReading(nodeA.data, 'Humidity'));
      const b = getSortableNumber(getAbilityReading(nodeB.data, 'Humidity'));
      return compareNullableNumbers(a, b);
    },
  },
  {
    headerName: 'Vibration',
    sortable: true,
    valueGetter(params) {
      const reading = getAbilityReading(params.data, 'Vibration');
      return reading ? ABILITY_MAP[reading.ability].value(reading) : '--';
    },
    comparator: (_valueA, _valueB, nodeA, nodeB) => {
      const a = getSortableNumber(getAbilityReading(nodeA.data, 'Vibration'));
      const b = getSortableNumber(getAbilityReading(nodeB.data, 'Vibration'));
      return compareNullableNumbers(a, b);
    },
  },
  {
    headerName: 'Pressure',
    sortable: true,
    valueGetter(params) {
      const reading = getPressureReading(params.data);
      return reading ? ABILITY_MAP[reading.ability].value(reading) : '--';
    },
    comparator: (_valueA, _valueB, nodeA, nodeB) => {
      const a = getSortableNumber(getPressureReading(nodeA.data));
      const b = getSortableNumber(getPressureReading(nodeB.data));
      return compareNullableNumbers(a, b);
    },
  },
];

export const gatewaysColumnDefs = [
  {
    headerName: '',
    field: 'isOnline',
    sortable: true,
    width: 80,
    minWidth: 80,
    maxWidth: 80,
    suppressMovable: true,
    cellRenderer: OnlineStatusCellRenderer,
    valueGetter: ({ data }) => (data.isOnline ? 1 : 0),
    headerClass: 'p-2',
    sortingOrder: ['asc', 'desc', null],
  },
  {
    headerName: 'Gateway',
    field: 'name',
    sortable: true,
    sortingOrder: ['asc', 'desc', null],
  },

  {
    headerName: 'MAC ID',
    field: 'deviceId',
    sortable: true,
    sortingOrder: ['asc', 'desc', null],
  },
];

export const assetColumnDefs = [
  {
    headerName: '',
    field: 'status',
    cellRenderer: OnlineStatusCellRenderer,
    valueGetter: ({ data }) => ([0, 1, 2, 3].includes(data.status) ? data.status : 0),
    width: 60,
    minWidth: 60,
    maxWidth: 60,
  },
  {
    headerName: 'Name',
    field: 'name',
    colId: 'name',
    cellRenderer: LinkCellRenderer,
    cellRendererParams: ({ data }) => ({
      to: `/assets/${data.id}`,
      text: data.name || '--',
    }),
  },
  {
    headerName: 'Last Check-In',
    field: 'lastCheckin',
    valueFormatter(params) {
      return getFormattedDateTime(params.value);
    },
  },
  {
    headerName: 'Readings',
    field: 'beacons',
    colId: 'readings',
    sortable: false,
    cellRenderer: BeaconReadingsCellRenderer,
    valueFormatter() {
      return;
    },
  },
];
